import { Fragment, h } from 'preact';
import { useContext } from 'preact/hooks';
import classNames from 'classnames';
import {
  ConnectionContext,
  KBContext,
  NavigationContext,
} from 'widget_main/store';
import { useTranslations } from 'i18n/hooks';
import WifiStrengthOffOutline from 'widget_main/globals/assets/wifiStrengthOffOutline.svg';
import {
  CONVERSATIONS_PATH,
  MESSAGE_THREAD_PATH,
  MODAL_ROOT_ID,
  KB_SEARCH_PATH,
  KB_ARTICLE_PATH,
  KB_DEFLECT_ARTICLE_PATH,
} from 'widget_main/globals/constants';

import ErrorBoundary from 'widget_main/components/ErrorBoundary';
import { useLocale, useVendors } from 'widget_main/vendors/hooks';
import Loader from 'widget_main/components/Loader';
import TitleBarButtons from '../Header/TitleBarButtons';
import ConversationsHeader from '../Header/ConversationsHeader';
import MessageThreadHeader from '../Header/MessageThreadHeader';
import Conversations from '../Conversations';
import MessageThread from '../MessageThread';
import KBSearchList from '../KBSearchList';
import Footer from '../Footer';
import styles from './widget.scss';
import KBHeader from '../Header/KBHeader';
import KBArticle from '../KBArticle/KBArticle';

import messages from './messages';

interface WidgetProps {
  isMobile: boolean;

  hideHistory?: boolean;
}

const Widget = ({ isMobile, hideHistory }: WidgetProps) => {
  const navigationState = useContext(NavigationContext);
  const kb = useContext(KBContext);
  const translations = useTranslations(messages);
  const connection = useContext(ConnectionContext);
  const [, isLoadingVendors] = useVendors();
  const [isLoadingLocale] = useLocale();

  const isLoading = isLoadingVendors || isLoadingLocale;

  const onGoBackForMessageThreadPath = () => {
    navigationState.updatePage(CONVERSATIONS_PATH);
  };

  const renderHeader = () => {
    switch (navigationState.page) {
      case CONVERSATIONS_PATH:
        return (
          <Fragment>
            <TitleBarButtons />
            <ConversationsHeader />
          </Fragment>
        );
      case MESSAGE_THREAD_PATH:
        return (
          <Fragment>
            <TitleBarButtons
              onGoBack={hideHistory ? undefined : onGoBackForMessageThreadPath}
            />
            <MessageThreadHeader />
          </Fragment>
        );
      case KB_SEARCH_PATH: {
        const { currentCategoryId } = kb;

        let onGoBack;

        if (currentCategoryId) {
          const category = kb.categories?.[currentCategoryId];
          const parent = category?.parentCategory;
          onGoBack = parent
            ? () => kb.updateCurrentCategoryId(parent)
            : undefined;
        }

        return (
          <Fragment>
            <TitleBarButtons onGoBack={onGoBack} />
            <KBHeader />
          </Fragment>
        );
      }
      case KB_ARTICLE_PATH: {
        return (
          <Fragment>
            <TitleBarButtons
              onGoBack={() => {
                navigationState.updatePage(KB_SEARCH_PATH);
              }}
            />
            <KBHeader />
          </Fragment>
        );
      }
      case KB_DEFLECT_ARTICLE_PATH: {
        return (
          <Fragment>
            <TitleBarButtons
              onGoBack={() => {
                navigationState.updatePage(MESSAGE_THREAD_PATH);
              }}
            />
            <KBHeader />
          </Fragment>
        );
      }
      default:
        return (
          <Fragment>
            <TitleBarButtons />
            <ConversationsHeader />
          </Fragment>
        );
    }
  };

  const routeWithErrorBoundary = (node) => {
    return <ErrorBoundary page={navigationState.page}>{node}</ErrorBoundary>;
  };

  const renderRoute = () => {
    switch (navigationState.page) {
      case CONVERSATIONS_PATH:
        return routeWithErrorBoundary(<Conversations />);
      case MESSAGE_THREAD_PATH:
        return routeWithErrorBoundary(<MessageThread />);
      case KB_SEARCH_PATH:
        return routeWithErrorBoundary(<KBSearchList />);
      case KB_ARTICLE_PATH:
        return routeWithErrorBoundary(<KBArticle />);
      case KB_DEFLECT_ARTICLE_PATH:
        return routeWithErrorBoundary(<KBArticle />);
      default:
        return routeWithErrorBoundary(<Conversations />);
    }
  };

  const renderConnectionIndicator = () => {
    if (connection === 'offline')
      return (
        <div className={styles.connectionContainer}>
          <WifiStrengthOffOutline className={styles.wifiIcon} />
          <span>{translations.noInternetConnection}</span>
        </div>
      );

    return null;
  };

  return (
    <div
      class={classNames(styles.widgetWrapper, {
        [styles.mobile]: isMobile,
      })}
    >
      <div class={styles.widget}>
        {renderHeader()}
        {renderConnectionIndicator()}
        <div id={MODAL_ROOT_ID} className={styles.modalContainer}>
          {isLoading ? <Loader /> : renderRoute()}
          <Footer />
        </div>
      </div>
    </div>
  );
};

export default Widget;
